import type { User } from "users/user";
import type { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import type {
  ProposalNegotiationParty,
  ProposalResolutionState,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import {
  getProposalCollectionItemIdentity,
  getProposalCollectionItemReplacedIdentity,
  proposalCollectionItemHasIdentity,
  proposalCollectionItemReplaces,
} from "work/proposals/negotiation/proposal-operation-identity";
import {
  canonicalProposalCollectionItem,
  proposalSemanticJsonValuesEqual,
} from "work/proposals/negotiation/proposal-collection-codec";
import {
  isProposalResolutionTargetResolvable,
  proposalResolutionTargetIncomingAuthor,
} from "work/proposals/negotiation/proposal-resolution-target";
import { decodeProposalPointerSegment } from "work/proposals/negotiation/proposal-operation-projector";
import Proposal from "work/proposals/proposal";
import ProposalRedline, { type ProposalRedlineJSON } from "work/proposals/redlining/proposal-redline";

type FieldRedlineJSON = ProposalRedlineJSON["name"];
type FieldRedlineOriginalEntryJSON = FieldRedlineJSON["originalEntry"];
type FieldRedlineCurrentEntryJSON = FieldRedlineJSON["currentEntry"];
type FieldRedlineArrayJSON = Extract<ProposalRedlineJSON["clientPolicies"], { redlines: unknown }>;
type CollectionRedlineKey = "clientPolicies" | "vendorPolicies" | "conflicts" | "vendorTeam" | "feeSchedule";
type CollectionKind = "document" | "member" | "feeCategory";

type CollectionMapping = {
  path: string;
  redlineKey: CollectionRedlineKey;
  kind: CollectionKind;
};

const scalarRedlineKeysByPath = {
  "/name": "name",
  "/description": "description",
  "/responseDueBy": "responseDueBy",
  "/startDate": "startDate",
  "/endDate": "endDate",
  "/teamRestricted/value": "teamRestricted",
  "/conflictsCheckWaived/value": "conflictsCheckWaived",
  "/discount/percent": "discount",
} as const satisfies Record<string, keyof ProposalRedlineJSON>;

const collectionMappings: readonly CollectionMapping[] = [
  { path: "/clientPolicies/documents", redlineKey: "clientPolicies", kind: "document" },
  { path: "/vendorPolicies/documents", redlineKey: "vendorPolicies", kind: "document" },
  { path: "/conflicts/documents", redlineKey: "conflicts", kind: "document" },
  { path: "/vendorTeam/members", redlineKey: "vendorTeam", kind: "member" },
  { path: "/feeSchedule/categories", redlineKey: "feeSchedule", kind: "feeCategory" },
];

const missing = Symbol("missing proposal resolution value");
type MaybeMissing = unknown | typeof missing;

/**
 * Builds the active editor from the canonical projection and typed resolution
 * state. Legacy unresolved-field and saved-redline snapshots are deliberately
 * removed before ProposalRedlineFactory runs, so canonical rendering has one
 * source of truth.
 */
export function proposalFromCanonicalNegotiationJSON(params: {
  proposalJSON: ProposalJSON;
  currentUser: User;
  resolutionStates: readonly ProposalResolutionState[];
  currentSubmissionId: string;
  viewingParty: ProposalNegotiationParty;
  /**
   * The viewer's last-submitted projection (the undo target). When supplied, a
   * plain locally-edited field — one with no incoming resolution target — is
   * diffed against this baseline so it exposes an undo affordance and reverts to
   * the last-submitted value. Omit it (or pass the projection itself as
   * `proposalJSON`) to render with no plain-edit baseline.
   */
  projectionBaseline?: ProposalJSON;
}): Proposal | null | undefined {
  const canonicalJSON = withoutLegacyRedlineResolutionMetadata(params.proposalJSON);
  const proposal = Proposal.fromJSON({
    json: canonicalJSON,
    currentUser: params.currentUser,
    options: { withRedline: true },
  });
  if (!proposal?.redline) return proposal;

  const resolvedRedline = applyProposalResolutionStatesToRedline({
    redline: proposal.redline,
    proposalJSON: canonicalJSON,
    resolutionStates: params.resolutionStates,
    currentSubmissionId: params.currentSubmissionId,
    viewingParty: params.viewingParty,
  });

  const redline = applyProjectionBaselineToRedline({
    redline: resolvedRedline,
    effectiveJSON: canonicalJSON,
    projectionJSON: params.projectionBaseline,
    resolutionStates: params.resolutionStates,
    currentSubmissionId: params.currentSubmissionId,
    viewingParty: params.viewingParty,
  });

  // Supplying the derived redline without deriving a second time preserves its
  // canonical decisions without incorrectly marking them as unsaved UI edits.
  return Proposal.fromJSON({
    json: canonicalJSON,
    currentUser: params.currentUser,
    existingRedline: redline,
    options: { withRedline: false, persistedRedline: redline },
  });
}

/**
 * Diffs each plain locally-edited field — scalar *and* collection item —
 * against the projection baseline (the viewer's last-submitted value) so a
 * change the user made with no incoming resolution target exposes an undo
 * affordance and reverts to that baseline through the existing redline write
 * path.
 *
 * This is intentionally disjoint from `applyProposalResolutionStatesToRedline`:
 * a field or collection item owned by a current, resolvable resolution target
 * is skipped entirely, so its mine/theirs rendering is never touched. A plain
 * edit sets `currentEntry` to the effective (edited) value, so the field stays
 * *resolved* (submittable) while carrying a diff against `original === revised
 * ===` baseline — undoable, never a phantom "changes requested" issue.
 *
 * Collections are reconciled by stable identity against the same baseline:
 *   - present in effective but not projection  → a local **add** (row's
 *     original/revised cleared to absent, current kept; undo removes it);
 *   - present in projection but not effective  → a local **remove** (a synthetic
 *     row original/revised = baseline item, current = null; undo restores it);
 *   - present in both with a different canonical value → a local **edit**
 *     (original/revised = baseline item, current kept; undo reverts to it).
 * An item whose value already matches the projection — for example a fee change
 * merged from the counterparty over websocket, which lands in the authoritative
 * projection — produces no diff and is never mistaken for the viewer's own work.
 */
export function applyProjectionBaselineToRedline(params: {
  redline: ProposalRedline;
  effectiveJSON: ProposalJSON;
  projectionJSON: ProposalJSON | undefined;
  resolutionStates: readonly ProposalResolutionState[];
  currentSubmissionId: string;
  viewingParty: ProposalNegotiationParty;
}): ProposalRedline {
  if (!params.projectionJSON) return params.redline;

  const currentResolvableStates = params.resolutionStates.filter(
    (state) =>
      state.submissionId === params.currentSubmissionId &&
      isProposalResolutionTargetResolvable(state, params.viewingParty),
  );
  const resolutionOwnedScalarPaths = new Set(
    currentResolvableStates.filter((state) => !state.itemId).map((state) => state.path),
  );
  const resolutionOwnedCollectionItems = new Set(
    currentResolvableStates
      .filter((state) => state.itemId)
      .map((state) => collectionItemOwnershipKey(state.path, state.itemId!)),
  );

  // Compute the plain-edit diffs from the two JSONs before touching the redline.
  // Effective === projection means no local edit for this field (only a queued
  // content operation can diverge them — reviewer changes never touch scalars),
  // so an unedited load applies nothing and never serializes the redline.
  const edits: { redlineKey: keyof ProposalRedlineJSON; projectionValue: MaybeMissing; effectiveValue: MaybeMissing }[] =
    [];
  for (const [path, redlineKey] of Object.entries(scalarRedlineKeysByPath)) {
    if (resolutionOwnedScalarPaths.has(path)) continue;
    const projectionValue = readJSONPointer(params.projectionJSON, path);
    const effectiveValue = readJSONPointer(params.effectiveJSON, path);
    if (semanticValuesEqual(projectionValue, effectiveValue)) continue;
    edits.push({ redlineKey, projectionValue, effectiveValue });
  }

  const collectionChanges = collectionProjectionBaselineChanges({
    projectionJSON: params.projectionJSON,
    effectiveJSON: params.effectiveJSON,
    resolutionOwnedCollectionItems,
  });

  if (edits.length === 0 && collectionChanges.length === 0) return params.redline;

  const redlineJSON = structuredClone(params.redline.toJSON());
  let changed = false;
  for (const { redlineKey, projectionValue, effectiveValue } of edits) {
    const fieldRedline = redlineJSON[redlineKey];
    if (!isFieldRedlineJSON(fieldRedline)) continue;

    // No incoming revision, so revised === original === baseline; the local edit
    // is the current value. `undo()` then reverts current to the baseline.
    fieldRedline.originalEntry = (projectionValue === missing ? null : clone(projectionValue)) as FieldRedlineOriginalEntryJSON;
    fieldRedline.revisedEntry = (projectionValue === missing ? null : clone(projectionValue)) as FieldRedlineOriginalEntryJSON;
    fieldRedline.currentEntry = (effectiveValue === missing ? null : clone(effectiveValue)) as FieldRedlineCurrentEntryJSON;
    fieldRedline.textChangeCollection = undefined;
    fieldRedline.incomingAuthor = undefined;
    changed = true;
  }

  for (const change of collectionChanges) {
    if (applyCollectionBaselineChange(redlineJSON, change)) changed = true;
  }

  if (!changed) return params.redline;
  return ProposalRedline.fromJSON(redlineJSON) ?? params.redline;
}

type CollectionBaselineChange = {
  redlineKey: CollectionRedlineKey;
  kind: "add" | "edit" | "remove";
  itemId: string;
  /** Baseline (last-submitted) item in redline row shape; absent for a pure add. */
  baselineEntry?: unknown;
};

/**
 * Reconciles every collaborative collection against the projection baseline by
 * stable identity, yielding the add/remove/edit set that gives each locally
 * changed item a diff and an undo affordance. Items owned by a current,
 * resolvable resolution target are skipped so their mine/theirs rendering,
 * already applied by `applyProposalResolutionStatesToRedline`, is left intact.
 */
function collectionProjectionBaselineChanges(params: {
  projectionJSON: ProposalJSON;
  effectiveJSON: ProposalJSON;
  resolutionOwnedCollectionItems: ReadonlySet<string>;
}): CollectionBaselineChange[] {
  const changes: CollectionBaselineChange[] = [];
  for (const mapping of collectionMappings) {
    const projectionItems = readCollectionItems(params.projectionJSON, mapping.path);
    const effectiveItems = readCollectionItems(params.effectiveJSON, mapping.path);
    if (!projectionItems && !effectiveItems) continue;

    const projectionById = collectionItemsByIdentity(projectionItems ?? []);
    const effectiveById = collectionItemsByIdentity(effectiveItems ?? []);
    // Baselines already claimed by the document that replaced them. A replace diffs as an add plus a
    // removal, but it is one change to one item: the replacement's row carries the replaced document
    // as its baseline, so the removal must not also synthesize a row of its own.
    const replacedBaselineIds = new Set<string>();

    for (const [itemId, effectiveItem] of effectiveById) {
      if (params.resolutionOwnedCollectionItems.has(collectionItemOwnershipKey(mapping.path, itemId))) continue;
      const replacedBaselineId = replacedProjectionBaselineId({
        effectiveItem,
        projectionById,
        effectiveById,
        resolutionOwnedCollectionItems: params.resolutionOwnedCollectionItems,
        path: mapping.path,
      });
      if (replacedBaselineId) replacedBaselineIds.add(replacedBaselineId);

      const projectionItem =
        projectionById.get(itemId) ?? (replacedBaselineId ? projectionById.get(replacedBaselineId) : undefined);
      if (projectionItem === undefined) {
        changes.push({ redlineKey: mapping.redlineKey, kind: "add", itemId });
      } else if (!collectionItemsCanonicalEqual(mapping.path, projectionItem, effectiveItem)) {
        changes.push({
          redlineKey: mapping.redlineKey,
          kind: "edit",
          itemId,
          baselineEntry: normalizeCollectionItem(projectionItem, mapping.kind),
        });
      }
    }

    for (const [itemId, projectionItem] of projectionById) {
      if (effectiveById.has(itemId)) continue;
      if (replacedBaselineIds.has(itemId)) continue;
      if (params.resolutionOwnedCollectionItems.has(collectionItemOwnershipKey(mapping.path, itemId))) continue;
      changes.push({
        redlineKey: mapping.redlineKey,
        kind: "remove",
        itemId,
        baselineEntry: normalizeCollectionItem(projectionItem, mapping.kind),
      });
    }
  }
  return changes;
}

/**
 * The baseline `effectiveItem` inherits by replacement, if any: the id of the document it declares it
 * replaces, when that document is in the baseline projection and has genuinely left the effective
 * collection.
 *
 * Absent when both documents are still present — that is two items, not a replace — and absent when
 * the replaced item is owned by a current resolution target, which renders it itself.
 */
function replacedProjectionBaselineId(params: {
  effectiveItem: unknown;
  projectionById: ReadonlyMap<string, unknown>;
  effectiveById: ReadonlyMap<string, unknown>;
  resolutionOwnedCollectionItems: ReadonlySet<string>;
  path: string;
}): string | undefined {
  const replacedId = getProposalCollectionItemReplacedIdentity(params.effectiveItem);
  if (!replacedId) return undefined;
  if (params.effectiveById.has(replacedId) || !params.projectionById.has(replacedId)) return undefined;
  if (params.resolutionOwnedCollectionItems.has(collectionItemOwnershipKey(params.path, replacedId))) {
    return undefined;
  }
  return replacedId;
}

function applyCollectionBaselineChange(
  redlineJSON: ProposalRedlineJSON,
  change: CollectionBaselineChange,
): boolean {
  const collectionRedline = redlineJSON[change.redlineKey];
  // A masked collection has no editable redline rows; leave it untouched.
  if (!isFieldRedlineArrayJSON(collectionRedline)) return false;

  const baselineEntry =
    change.baselineEntry === missing || change.baselineEntry === undefined
      ? null
      : (clone(change.baselineEntry) as FieldRedlineOriginalEntryJSON);

  if (change.kind === "remove") {
    // The item is absent from the effective editor, so no row was built for it.
    // Synthesize one whose baseline is the last-submitted item and whose current
    // value is removed: a newly-removed row the user can undo to restore it.
    if (findCollectionItemRedline(collectionRedline.redlines, change.itemId)) return false;
    collectionRedline.redlines.push({
      originalEntry: baselineEntry,
      revisedEntry: baselineEntry,
      currentEntry: null as FieldRedlineCurrentEntryJSON,
      sessionHistory: [],
      diffWords: false,
      textChangeCollection: undefined,
    });
    return true;
  }

  const row = findCollectionItemRedline(collectionRedline.redlines, change.itemId);
  if (!row) return false;

  // A local add has no baseline, so original === revised === absent and the
  // effective item is the current value (undo removes it). A local edit anchors
  // original === revised at the last-submitted item, keeping the effective item
  // as current (undo reverts to the baseline). Either way, current stays the
  // effective value already built from the projection, so the field is resolved
  // and re-flatten produces no spurious operation.
  row.originalEntry = (change.kind === "add" ? null : baselineEntry) as FieldRedlineOriginalEntryJSON;
  row.revisedEntry = (change.kind === "add" ? null : baselineEntry) as FieldRedlineOriginalEntryJSON;
  if ("textChangeCollection" in row) row.textChangeCollection = undefined;
  row.incomingAuthor = undefined;
  return true;
}

function collectionItemOwnershipKey(path: string, itemId: string): string {
  return `${path}::${itemId.toLowerCase()}`;
}

/** Reads a collection as an array, treating an absent parent/collection as empty and a masked value as unavailable. */
function readCollectionItems(proposalJSON: ProposalJSON, path: string): unknown[] | undefined {
  const value = readJSONPointer(proposalJSON, path);
  if (value === missing) return [];
  return Array.isArray(value) ? value : undefined;
}

function collectionItemsByIdentity(items: readonly unknown[]): Map<string, unknown> {
  const byId = new Map<string, unknown>();
  for (const item of items) {
    const itemId = getProposalCollectionItemIdentity(item);
    if (itemId) byId.set(itemId, item);
  }
  return byId;
}

function collectionItemsCanonicalEqual(path: string, left: unknown, right: unknown): boolean {
  return proposalSemanticJsonValuesEqual(
    canonicalProposalCollectionItem(path, left),
    canonicalProposalCollectionItem(path, right),
  );
}

/**
 * Projects exact canonical resolution targets onto the existing redline model.
 * Scalar decisions stay scalar and collection decisions affect only their
 * stable item. Multiple resolved leaves of one collection item are merged into
 * a single current entry; if any leaf is unresolved, that item alone remains
 * unresolved in the existing item-level review widget.
 */
export function applyProposalResolutionStatesToRedline(params: {
  redline: ProposalRedline;
  proposalJSON: ProposalJSON;
  resolutionStates: readonly ProposalResolutionState[];
  currentSubmissionId: string;
  viewingParty: ProposalNegotiationParty;
}): ProposalRedline {
  const states = params.resolutionStates.filter(
    (state) =>
      state.submissionId === params.currentSubmissionId &&
      isProposalResolutionTargetResolvable(state, params.viewingParty),
  );
  if (states.length === 0) return params.redline;
  const redlineJSON = structuredClone(params.redline.toJSON());
  clearRedlineSessionHistory(redlineJSON);
  applyScalarResolutionStates(redlineJSON, params.proposalJSON, states);
  applyCollectionResolutionStates(redlineJSON, params.proposalJSON, states);
  return ProposalRedline.fromJSON(redlineJSON) ?? params.redline;
}

function applyScalarResolutionStates(
  redlineJSON: ProposalRedlineJSON,
  proposalJSON: ProposalJSON,
  states: readonly ProposalResolutionState[],
): void {
  for (const state of states) {
    const redlineKey = scalarRedlineKeysByPath[state.path as keyof typeof scalarRedlineKeysByPath];
    if (!redlineKey) continue;

    const fieldRedline = redlineJSON[redlineKey];
    if (!isFieldRedlineJSON(fieldRedline)) continue;

    fieldRedline.originalEntry = scalarPresentationOriginalEntry(state, proposalJSON) as FieldRedlineOriginalEntryJSON;
    fieldRedline.revisedEntry = (state.targetValueExists ? clone(state.targetValue) : null) as FieldRedlineOriginalEntryJSON;
    fieldRedline.currentEntry = scalarCurrentEntry(state, proposalJSON) as FieldRedlineCurrentEntryJSON;
    fieldRedline.textChangeCollection = undefined;
    fieldRedline.incomingAuthor = proposalResolutionTargetIncomingAuthor(state);
  }
}

function scalarPresentationOriginalEntry(
  state: ProposalResolutionState,
  proposalJSON: ProposalJSON,
): unknown {
  const baseline = state.baselineValueExists ? state.baselineValue : missing;
  if (state.disposition !== "Unresolved") return baseline === missing ? null : clone(baseline);

  const effective = readJSONPointer(proposalJSON, state.path);
  const target = state.targetValueExists ? state.targetValue : missing;
  const presentationBaseline = semanticValuesEqual(effective, target) ? baseline : effective;
  return presentationBaseline === missing ? null : clone(presentationBaseline);
}

function scalarCurrentEntry(state: ProposalResolutionState, proposalJSON: ProposalJSON): unknown {
  switch (state.disposition) {
    case "Unresolved":
      return undefined;
    case "AcceptIncoming":
      return state.targetValueExists ? clone(state.targetValue) : null;
    case "KeepPrevious":
      return state.baselineValueExists ? clone(state.baselineValue) : null;
    case "ProposeAlternative": {
      const proposedValue = readJSONPointer(proposalJSON, state.path);
      if (proposedValue !== missing) return clone(proposedValue);
      return null;
    }
  }
}

function applyCollectionResolutionStates(
  redlineJSON: ProposalRedlineJSON,
  proposalJSON: ProposalJSON,
  states: readonly ProposalResolutionState[],
): void {
  for (const mapping of collectionMappings) {
    const collectionStates = states.filter((state) => state.path === mapping.path && state.itemId);
    if (collectionStates.length === 0) continue;

    const collectionRedline = redlineJSON[mapping.redlineKey];
    if (!isFieldRedlineArrayJSON(collectionRedline)) continue;

    const statesByItemId = groupByItemId(collectionStates);
    const projectedCollection = readJSONPointer(proposalJSON, mapping.path);
    const projectedItems = Array.isArray(projectedCollection) ? projectedCollection : [];
    foldReplacedItemStates(statesByItemId, projectedItems);

    for (const [itemId, itemStates] of statesByItemId) {
      const itemRedline = ensureCollectionItemRedline({
        collectionRedline,
        itemId,
        itemStates,
        projectedItems,
        mapping,
      });
      if (!itemRedline) continue;

      const projectedItem = projectedItems.find((item) => proposalCollectionItemHasIdentity(item, itemId));
      const itemParams = {
        itemRedline,
        itemStates,
        projectedItem: normalizeCollectionItem(projectedItem, mapping.kind),
        mapping,
      };
      const canonicalEntries = canonicalCollectionItemEntries(itemParams);
      const presentationEntries = collectionPresentationEntries({
        ...itemParams,
        ...canonicalEntries,
      });
      itemRedline.originalEntry = presentationEntries.originalEntry as FieldRedlineOriginalEntryJSON;
      itemRedline.revisedEntry = presentationEntries.revisedEntry as FieldRedlineOriginalEntryJSON;
      itemRedline.currentEntry = collectionItemCurrentEntry({
        ...itemParams,
        ...presentationEntries,
      }) as FieldRedlineCurrentEntryJSON;
      // Recompute text changes for the exact current value instead of retaining
      // a diff serialized for an earlier presentation snapshot.
      if ("textChangeCollection" in itemRedline) itemRedline.textChangeCollection = undefined;
      const authorState = itemStates.find((state) => collectionLeafSegments(state).length === 0) ?? itemStates[0];
      if (authorState) itemRedline.incomingAuthor = proposalResolutionTargetIncomingAuthor(authorState);
    }
  }
}

/**
 * Folds a replaced document's resolution targets into the group of the document that replaced it.
 *
 * A replace is published as two targets — an add for the replacement, a removal of what it replaced —
 * because that is how it diffs, but to the reader it is one change to one row. Left as two groups the
 * removal has no item left in the projection to name it: `collectionEntryForSyntheticRedline`
 * deliberately renders an unresolvable document as absent rather than crashing the decoder, so it
 * surfaced as a row with no name at all sitting beside the replacement. Folded, the replacement's row
 * owns both targets and stays unresolved until both are decided.
 */
function foldReplacedItemStates(
  statesByItemId: Map<string, ProposalResolutionState[]>,
  projectedItems: readonly unknown[],
): void {
  for (const [itemId, itemStates] of [...statesByItemId]) {
    // Only once the replaced document has really left the collection. While both are present they
    // are two items, and each keeps its own row.
    if (projectedItems.some((item) => proposalCollectionItemHasIdentity(item, itemId))) continue;

    const replacement = projectedItems.find((item) => proposalCollectionItemReplaces(item, itemId));
    const replacementId = replacement === undefined ? undefined : getProposalCollectionItemIdentity(replacement);
    const replacementStates = replacementId === undefined ? undefined : statesByItemId.get(replacementId);
    if (!replacementStates) continue;

    // Appended, never prepended: the replacement's own whole-item state stays first, so the row keeps
    // reading as the incoming add it is, and `itemStates.some(Unresolved)` now covers both halves.
    replacementStates.push(...itemStates);
    statesByItemId.delete(itemId);
  }
}

function ensureCollectionItemRedline(params: {
  collectionRedline: FieldRedlineArrayJSON;
  itemId: string;
  itemStates: readonly ProposalResolutionState[];
  projectedItems: readonly unknown[];
  mapping: CollectionMapping;
}): FieldRedlineJSON | undefined {
  const existing = findCollectionItemRedline(params.collectionRedline.redlines, params.itemId);
  if (existing) return existing;

  const wholeItemState = params.itemStates.find((state) => collectionLeafSegments(state).length === 0);
  const projectedItem = normalizeCollectionItem(
    params.projectedItems.find((item) => proposalCollectionItemHasIdentity(item, params.itemId)),
    params.mapping.kind,
  );

  // First submissions and other canonical-only handoffs may have a durable
  // ResolutionTargetPublished event before any legacy supersedes-derived
  // redline row exists. In that case the target itself is the source of the
  // presentation row. Without this, incoming whole-item adds render as an empty
  // collection and cannot be accepted/rejected.
  if (!wholeItemState && projectedItem === missing) return undefined;

  const originalEntry = collectionEntryForSyntheticRedline({
    state: wholeItemState,
    valueKind: "baseline",
    projectedItem,
    path: params.mapping.path,
    kind: params.mapping.kind,
  });
  const revisedEntry = collectionEntryForSyntheticRedline({
    state: wholeItemState,
    valueKind: "target",
    projectedItem,
    path: params.mapping.path,
    kind: params.mapping.kind,
  });
  const synthetic: FieldRedlineJSON = {
    originalEntry: (originalEntry === missing ? null : clone(originalEntry)) as FieldRedlineOriginalEntryJSON,
    revisedEntry: (revisedEntry === missing ? null : clone(revisedEntry)) as FieldRedlineOriginalEntryJSON,
    currentEntry: undefined,
    sessionHistory: [],
    diffWords: false,
    textChangeCollection: undefined,
  };
  params.collectionRedline.redlines.push(synthetic);
  return synthetic;
}

function collectionEntryForSyntheticRedline(params: {
  state: ProposalResolutionState | undefined;
  valueKind: "baseline" | "target";
  projectedItem: MaybeMissing;
  path: string;
  kind: CollectionKind;
}): MaybeMissing {
  if (!params.state) return params.projectedItem;

  const exists = params.valueKind === "baseline"
    ? params.state.baselineValueExists
    : params.state.targetValueExists;
  if (!exists) return null;

  const stateValue = params.valueKind === "baseline"
    ? params.state.baselineValue
    : params.state.targetValue;
  if (params.projectedItem !== missing) {
    return hydrateWholeCollectionEntry({
      authorizedEntry: params.projectedItem,
      stateValue,
      kind: params.kind,
      path: params.path,
    });
  }

  // A document with no item left in the projection has no resolvable display metadata; returning a
  // canonical reference here would fail WorkDocument.fromJSON in the redline decoder
  // (InvalidWorkDocumentJSON crash). Render it as absent instead. Other collection kinds' decoders
  // tolerate a canonical value, so keep the canonical fallback for them.
  if (params.kind === "document") return missing;
  return canonicalProposalCollectionItem(params.path, stateValue);
}

function collectionItemCurrentEntry(params: {
  itemRedline: FieldRedlineJSON;
  itemStates: readonly ProposalResolutionState[];
  projectedItem: MaybeMissing;
  mapping: CollectionMapping;
  originalEntry: unknown;
  revisedEntry: unknown;
}): unknown {
  if (params.itemStates.some((state) => state.disposition === "Unresolved")) return undefined;

  const wholeItemState = params.itemStates.find((state) => collectionLeafSegments(state).length === 0);
  let currentEntry: MaybeMissing = wholeItemState
    ? wholeCollectionItemValue(wholeItemState, {
        projectedItem: params.projectedItem,
        originalEntry: params.originalEntry,
        revisedEntry: params.revisedEntry,
      })
    : firstPresent(
        params.projectedItem,
        params.itemRedline.currentEntry === undefined ? missing : params.itemRedline.currentEntry,
        params.originalEntry,
        params.revisedEntry,
      );

  if (currentEntry === missing) currentEntry = null;
  // A whole-item add/remove decision owns item existence. Rejecting an add or
  // accepting a removal must remain null even if the same submission also
  // produced leaf states for that item.
  if (wholeItemState && currentEntry === null) return null;

  for (const state of params.itemStates) {
    const operationLeafSegments = collectionLeafSegments(state);
    if (operationLeafSegments.length === 0) continue;

    const redlineLeafSegments = redlineCollectionLeafSegments(operationLeafSegments, params.mapping.kind);
    const selected = selectedCollectionLeafValue({
      state,
      redlineLeafSegments,
      originalEntry: params.originalEntry,
      revisedEntry: params.revisedEntry,
      projectedItem: params.projectedItem,
    });
    currentEntry = writeNestedValue(currentEntry, redlineLeafSegments, selected);
  }

  return currentEntry === missing ? null : clone(currentEntry);
}

function canonicalCollectionItemEntries(params: {
  itemRedline: FieldRedlineJSON;
  itemStates: readonly ProposalResolutionState[];
  projectedItem: MaybeMissing;
  mapping: CollectionMapping;
}): { originalEntry: unknown; revisedEntry: unknown } {
  const wholeItemState = params.itemStates.find((state) => collectionLeafSegments(state).length === 0);
  const itemId = params.itemStates[0]?.itemId;
  const authorizedOriginalEntry = findAuthorizedCollectionEntry(itemId, [
    params.itemRedline.originalEntry,
    params.itemRedline.currentEntry,
    params.projectedItem,
    params.itemRedline.revisedEntry,
  ]);
  const authorizedRevisedEntry = findAuthorizedCollectionEntry(itemId, [
    params.itemRedline.revisedEntry,
    params.projectedItem,
    params.itemRedline.currentEntry,
    params.itemRedline.originalEntry,
  ]);
  let originalEntry: MaybeMissing;
  let revisedEntry: MaybeMissing;

  if (wholeItemState) {
    originalEntry = wholeItemState.baselineValueExists
      ? hydrateWholeCollectionEntry({
          authorizedEntry: authorizedOriginalEntry,
          stateValue: wholeItemState.baselineValue,
          kind: params.mapping.kind,
          path: params.mapping.path,
        })
      : null;
    revisedEntry = wholeItemState.targetValueExists
      ? hydrateWholeCollectionEntry({
          authorizedEntry: authorizedRevisedEntry,
          stateValue: wholeItemState.targetValue,
          kind: params.mapping.kind,
          path: params.mapping.path,
        })
      : null;
  } else {
    originalEntry = authorizedOriginalEntry;
    revisedEntry = authorizedRevisedEntry;
  }

  for (const state of params.itemStates) {
    const operationLeafSegments = collectionLeafSegments(state);
    if (operationLeafSegments.length === 0) continue;
    const redlineLeafSegments = redlineCollectionLeafSegments(operationLeafSegments, params.mapping.kind);

    // Whole-item absence dominates leaf values. This occurs when one submitted
    // command adds/removes an item and also updates one of its leaves.
    if (originalEntry !== null) {
      originalEntry = writeNestedValue(
        originalEntry,
        redlineLeafSegments,
        canonicalCollectionLeafValue({
          state,
          valueKind: "baseline",
          redlineLeafSegments,
          authorizedEntry: authorizedOriginalEntry,
          collectionKind: params.mapping.kind,
        }),
      );
    }
    if (revisedEntry !== null) {
      revisedEntry = writeNestedValue(
        revisedEntry,
        redlineLeafSegments,
        canonicalCollectionLeafValue({
          state,
          valueKind: "target",
          redlineLeafSegments,
          authorizedEntry: authorizedRevisedEntry,
          collectionKind: params.mapping.kind,
        }),
      );
    }
  }

  return {
    originalEntry: originalEntry === missing ? null : clone(originalEntry),
    revisedEntry: revisedEntry === missing ? null : clone(revisedEntry),
  };
}

function wholeCollectionItemValue(
  state: ProposalResolutionState,
  params: {
    projectedItem: MaybeMissing;
    originalEntry: unknown;
    revisedEntry: unknown;
  },
): MaybeMissing {
  switch (state.disposition) {
    case "Unresolved":
      return missing;
    case "AcceptIncoming":
      return params.revisedEntry;
    case "KeepPrevious":
      return params.originalEntry;
    case "ProposeAlternative":
      return params.projectedItem === missing ? null : params.projectedItem;
  }
}

function selectedCollectionLeafValue(params: {
  state: ProposalResolutionState;
  redlineLeafSegments: readonly string[];
  originalEntry: unknown;
  revisedEntry: unknown;
  projectedItem: MaybeMissing;
}): MaybeMissing {
  switch (params.state.disposition) {
    case "Unresolved":
      return missing;
    case "AcceptIncoming":
      return readNestedValue(params.revisedEntry, params.redlineLeafSegments);
    case "KeepPrevious":
      return readNestedValue(params.originalEntry, params.redlineLeafSegments);
    case "ProposeAlternative":
      return readNestedValue(params.projectedItem, params.redlineLeafSegments);
  }
}

function canonicalCollectionLeafValue(params: {
  state: ProposalResolutionState;
  valueKind: "baseline" | "target";
  redlineLeafSegments: readonly string[];
  authorizedEntry: MaybeMissing;
  collectionKind: CollectionKind;
}): MaybeMissing {
  const authoritativeValue = readNestedValue(params.authorizedEntry, params.redlineLeafSegments);
  const exists =
    params.valueKind === "baseline" ? params.state.baselineValueExists : params.state.targetValueExists;
  if (!exists) return missing;
  // Document metadata is deliberately hydrated only from ProposalResponse and
  // the redline derived from its supersession history. Resolution state is an
  // existence/decision protocol, not another trusted document DTO.
  if (
    params.collectionKind === "document" &&
    !(params.redlineLeafSegments.length === 1 && params.redlineLeafSegments[0] === "replacesId")
  ) {
    return authoritativeValue === missing ? missing : clone(authoritativeValue);
  }

  const value = params.valueKind === "baseline" ? params.state.baselineValue : params.state.targetValue;
  // Typed resolution-state values are the raw semantic leaf. Presentation
  // hydration never reinterprets them as an operation payload shape.
  return value === undefined && authoritativeValue !== missing
    ? clone(authoritativeValue)
    : value;
}

function findAuthorizedCollectionEntry(
  itemId: string | null | undefined,
  candidates: readonly MaybeMissing[],
): MaybeMissing {
  if (!itemId) return missing;
  return (
    candidates.find(
      (candidate) => candidate !== missing && candidate !== null && proposalCollectionItemHasIdentity(candidate, itemId),
    ) ?? missing
  );
}

function hydrateWholeCollectionEntry(params: {
  authorizedEntry: MaybeMissing;
  stateValue: unknown;
  kind: CollectionKind;
  path: string;
}): MaybeMissing {
  if (params.authorizedEntry === missing || params.authorizedEntry === null) return missing;

  const hydrated = clone(params.authorizedEntry);
  if (!isRecord(hydrated) || !isRecord(params.stateValue)) return hydrated;

  if (params.kind === "feeCategory") {
    const canonicalState = canonicalProposalCollectionItem(params.path, params.stateValue);
    if (isRecord(canonicalState)) {
      for (const [property, value] of Object.entries(canonicalState)) hydrated[property] = clone(value);
    }
    return hydrated;
  }

  // Document replacement lineage is negotiation semantics and may be present
  // in stripped state. All document metadata remains the authorized value.
  if (
    params.kind === "document" &&
    Object.prototype.hasOwnProperty.call(params.stateValue, "replacesId")
  ) {
    hydrated.replacesId = clone(params.stateValue.replacesId);
  }
  return hydrated;
}

function collectionPresentationEntries(params: {
  itemStates: readonly ProposalResolutionState[];
  projectedItem: MaybeMissing;
  mapping: CollectionMapping;
  originalEntry: unknown;
  revisedEntry: unknown;
}): { originalEntry: unknown; revisedEntry: unknown } {
  if (!params.itemStates.some((state) => state.disposition === "Unresolved")) {
    return { originalEntry: params.originalEntry, revisedEntry: params.revisedEntry };
  }

  const effective = params.projectedItem;
  const target = params.revisedEntry === null ? missing : params.revisedEntry;
  const retainedLocal = collectionSemanticValuesEqual(effective, target, params.mapping.path)
    ? params.originalEntry
    : effective;
  return {
    originalEntry: retainedLocal === missing ? null : clone(retainedLocal),
    revisedEntry: params.revisedEntry,
  };
}

function groupByItemId(states: readonly ProposalResolutionState[]): Map<string, ProposalResolutionState[]> {
  const grouped = new Map<string, ProposalResolutionState[]>();
  for (const state of states) {
    if (!state.itemId) continue;
    const existing = grouped.get(state.itemId) ?? [];
    existing.push(state);
    grouped.set(state.itemId, existing);
  }
  return grouped;
}

function collectionLeafSegments(state: ProposalResolutionState): string[] {
  if (!state.itemId) return [];
  const prefix = `${state.path.slice(1).replaceAll("/", ":")}:${state.itemId}`;
  if (state.conflictKey === prefix) return [];
  if (!state.conflictKey.startsWith(`${prefix}:`)) return [];
  return state.conflictKey.slice(prefix.length + 1).split(":").filter(Boolean);
}

function redlineCollectionLeafSegments(segments: readonly string[], kind: CollectionKind): string[] {
  return kind === "document" && segments[0] === "documentInfo" ? segments.slice(1) : [...segments];
}

function normalizeCollectionItem(value: unknown, kind: CollectionKind): MaybeMissing {
  if (value === undefined || value === missing) return missing;
  if (kind !== "document") return clone(value);
  // A document redline entry must be a full WorkDocumentJSON. A canonical/wrapper value with no
  // resolvable documentInfo (e.g. a superseded add whose document left the projection) is not valid
  // WorkDocumentJSON and would crash WorkDocument.fromJSON in the redline decoder — treat it as absent.
  if (!isRecord(value) || !isRecord(value.documentInfo)) return missing;

  return {
    ...clone(value.documentInfo),
    id: clone(value.documentId),
    replacesId: clone(value.replacesId),
    isAvailable: clone(value.isAvailable),
  };
}

function redlineHasItemIdentity(redline: FieldRedlineJSON, itemId: string): boolean {
  return [redline.currentEntry, redline.revisedEntry, redline.originalEntry].some((entry) =>
    proposalCollectionItemHasIdentity(entry, itemId),
  );
}

/**
 * The row that stands for `itemId`: its own row, or — failing that — the row of the document that
 * REPLACED it.
 *
 * A replace is one change on one row. `FieldRedlineArray.create` has always collapsed it that way,
 * matching a revised/current entry to an original through `replacesId`, so the replacement's row is
 * where the replaced document's baseline, history and resolution belong. Without the lineage fallback
 * each half of a replace claims a row of its own: the replacement beside the document it replaced,
 * and — once the replaced document has really left the projection, taking its name with it — a row
 * with nothing left to render.
 *
 * The exact match is tried first and always wins, so a collection genuinely holding both documents
 * (which is not a replace, whatever `replacesId` says) still gives each its own row.
 */
function findCollectionItemRedline(
  redlines: readonly FieldRedlineJSON[],
  itemId: string,
): FieldRedlineJSON | undefined {
  return (
    redlines.find((redline) => redlineHasItemIdentity(redline, itemId)) ??
    redlines.find((redline) => redlineReplacesItemIdentity(redline, itemId))
  );
}

function redlineReplacesItemIdentity(redline: FieldRedlineJSON, itemId: string): boolean {
  return [redline.currentEntry, redline.revisedEntry, redline.originalEntry].some((entry) =>
    proposalCollectionItemReplaces(entry, itemId),
  );
}

function withoutLegacyRedlineResolutionMetadata(proposalJSON: ProposalJSON): ProposalJSON {
  const clone = structuredClone(proposalJSON);
  stripLegacyRedlineResolutionMetadata(clone);
  return clone;
}

function stripLegacyRedlineResolutionMetadata(proposalJSON?: ProposalJSON | null): void {
  if (!proposalJSON) return;
  delete proposalJSON.unresolvedFields;
  delete proposalJSON.existingRedlineJSON;
  stripLegacyRedlineResolutionMetadata(proposalJSON.partyRevision);
  stripLegacyRedlineResolutionMetadata(proposalJSON.supersedes);
}

function clearRedlineSessionHistory(value: unknown): void {
  if (!isRecord(value)) return;
  if (Array.isArray(value.sessionHistory)) value.sessionHistory = [];
  if (Array.isArray(value.redlines)) value.redlines.forEach(clearRedlineSessionHistory);
  for (const child of Object.values(value)) {
    if (isRecord(child)) clearRedlineSessionHistory(child);
  }
}

function readJSONPointer(value: unknown, pointer: string): MaybeMissing {
  if (!pointer.startsWith("/")) return missing;
  const segments = pointer.slice(1).split("/").map(decodeProposalPointerSegment);
  return readNestedValue(value, segments);
}

function readNestedValue(value: unknown, segments: readonly string[]): MaybeMissing {
  let current = value;
  for (const segment of segments) {
    if (!isRecord(current) || !Object.prototype.hasOwnProperty.call(current, segment)) return missing;
    current = current[segment];
  }
  return current;
}

function writeNestedValue(value: MaybeMissing, segments: readonly string[], selected: MaybeMissing): unknown {
  if (segments.length === 0) return selected === missing ? null : clone(selected);
  const root = isRecord(value) ? clone(value) : {};
  let parent: Record<string, unknown> = root;
  for (const segment of segments.slice(0, -1)) {
    const child = parent[segment];
    parent[segment] = isRecord(child) ? clone(child) : {};
    parent = parent[segment] as Record<string, unknown>;
  }

  const finalSegment = segments.at(-1)!;
  if (selected === missing) delete parent[finalSegment];
  else parent[finalSegment] = clone(selected);
  return root;
}

function firstPresent(...values: MaybeMissing[]): MaybeMissing {
  return values.find((value) => value !== missing) ?? missing;
}

function semanticValuesEqual(left: MaybeMissing, right: MaybeMissing): boolean {
  if (left === missing || right === missing) return left === right;
  return jsonValuesEqual(left, right);
}

function collectionSemanticValuesEqual(left: MaybeMissing, right: MaybeMissing, path: string): boolean {
  const leftExists = left !== missing && left !== null;
  const rightExists = right !== missing && right !== null;
  if (!leftExists || !rightExists) return leftExists === rightExists;
  return jsonValuesEqual(
    canonicalProposalCollectionItem(path, left),
    canonicalProposalCollectionItem(path, right),
  );
}

function jsonValuesEqual(left: unknown, right: unknown): boolean {
  return proposalSemanticJsonValuesEqual(left, right);
}

function isFieldRedlineJSON(value: unknown): value is FieldRedlineJSON {
  return isRecord(value) && "originalEntry" in value && "revisedEntry" in value;
}

function isFieldRedlineArrayJSON(value: unknown): value is FieldRedlineArrayJSON {
  return isRecord(value) && Array.isArray(value.redlines);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function clone<T>(value: T): T {
  return value === undefined || value === missing ? value : structuredClone(value);
}
